import { GooglePlaceTypesMap } from '@/models/locationModel';
import { buildGygSearchQueryFromLocation, type GygLocationInput } from './buildGygSearchQuery';

const STAY_CATEGORY_KEYWORDS = [
  'hotel',
  'lodging',
  'motel',
  'accommodation',
  'resort',
  'bed_and_breakfast',
  'hostel',
  'guest_house',
] as const;

function hasStayCategoryName(categoryName: string | undefined | null): boolean {
  if (!categoryName) {
    return false;
  }
  const lower = categoryName.toLowerCase();
  return STAY_CATEGORY_KEYWORDS.some((keyword) => lower.includes(keyword));
}

function hasFeaturedDoGoogleType(googleTypes: string[] | undefined | null): boolean {
  if (!Array.isArray(googleTypes) || googleTypes.length === 0) {
    return false;
  }
  const featuredSet = new Set<string>(GooglePlaceTypesMap.FEATURED_DO);
  return googleTypes.some((type) => featuredSet.has(type));
}

/**
 * Whether a place is eligible for GYG experiences (not eat/stay; do or featured attraction types).
 */
export function isExperienceBookablePlace(location: GygLocationInput | null | undefined): boolean {
  if (!location) {
    return false;
  }

  const gtCategory = location.gt_category;
  const categoryName = location.category_name;

  if (gtCategory === 'eat' || gtCategory === 'stay') {
    return false;
  }

  if (hasStayCategoryName(categoryName)) {
    return false;
  }

  if (gtCategory === 'do') {
    return true;
  }

  const googleTypes =
    location.google_types ??
    location.google_places_data?.types ??
    undefined;

  return hasFeaturedDoGoogleType(googleTypes);
}

/**
 * Eligible for GYG and has a resolvable search query for the widget/deep link.
 */
export function canShowGygForLocation(location: GygLocationInput | null | undefined): boolean {
  if (!isExperienceBookablePlace(location)) {
    return false;
  }
  return buildGygSearchQueryFromLocation(location) !== null;
}
